/* eslint-disable */
import {
	Autocomplete,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import { SelectPicker, DateRangePicker } from 'rsuite';
import { Filter } from 'lucide-react';
import moment from 'moment';
import { analyticsAction, formActions } from '../../Actions';
import styles from './ImsStockFilter.module.scss';
import { authHeader, classes, convertToHigherUnit, filtersInfoChip, handleExcelDownload, handleResponseStreamDownload, skuTypesList } from '../../Helpers';
import ImagesFinder from '../ImagesFinder/ImagesFinder';
import drawerActions from '@/Actions/drawer.actions';
import { Button } from '../ui/button';
import GlobalSearch from '../globalSearch/search';
import { getCurrentPageNavInfo } from '@/Helpers';
import assetServices from '../../Services/AssetServices/asset.services';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { notificationServices } from '@/Services';

const { combine, allowedMaxDays } = DateRangePicker;
// End date must be strictly before today — "current or future" is disallowed.
const disableTodayOrFutureDate = (date) =>
	moment(date).startOf('day').isSameOrAfter(moment().startOf('day'));
// Asset Wise Summary export: max range is a week, end date can't be today/future.
const ASSET_SUMMARY_MAX_RANGE_DAYS = 7;
const assetSummaryShouldDisableDate = combine(
	disableTodayOrFutureDate,
	allowedMaxDays(ASSET_SUMMARY_MAX_RANGE_DAYS)
);

function ImsStockFilter(props) {
	const {
		stockDeficit,
		setStockDeficit,
		type,
		filters,
		setFilters,
		resetInventoryAnalyticsState,
		data,
		setSearch,
		navState,
		assetId,
		setAssetId,
		setAssetName,
		permittedStores,
	} = props;

	const dispatch = useDispatch();
	const [assetsList, setAssetsList] = useState([]);
	const [assetsLoading, setAssetsLoading] = useState(false);
	// '' = ALL (default), 'true' = Yes, 'false' = No
	const [thirdPartySales, setThirdPartySales] = useState('');

	// Asset Wise Summary export is retail-only — it summarizes per-asset stock
	// movement (AssetProductStockDailySnapshot), which only retail stores populate.
	// `filters.store` only ever carries { id, name } (see ImagesFinder's SET_STORE
	// reducer), so the store's type has to be looked up from permittedStores.
	const isRetail = permittedStores?.find(
		(s) => s?.id === filters?.store?.id
	)?.storeType === 'RETAIL';
	const [assetSummaryModalOpen, setAssetSummaryModalOpen] = useState(false);
	const [assetSummaryDateRange, setAssetSummaryDateRange] = useState([]);
	const [assetSummaryExporting, setAssetSummaryExporting] = useState(false);

	const exportInfo = useMemo(() => ({
		showExportBtn: true,
		showDropDown: true,
		exportTypes: [
			{ label: 'Item Wise Stock', value: 'ITEM_WISE' },
			{ label: 'Batchwise Stock', value: 'BATCH_WISE' },
			...(isRetail
				? [{ label: 'Asset Wise Summary', value: 'ASSET_WISE_SUMMARY' }]
				: []),
		],
	}), [isRetail]);

	const handleAssetChange = (value) => {
		setAssetId(value);
		const asset = assetsList.find((asset) => asset.value === value);
		setAssetName(asset?.label);
	};

	// Fetch assets list from API
	const fetchAssetsList = async (storeId) => {
		if (!storeId) return;
		setAssetsLoading(true);
		try {
			const result = await assetServices.assetProductInventoryService({
				req: { data: { storeId, type: 'Display Freezer' } },
			});
			if (result?.status === 'Success' && result?.data?.assets) {
				const formattedAssets = result.data.assets.map((asset) => ({
					value: asset.id,
					label: asset.name,
				}));
				setAssetsList(formattedAssets);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching assets:', error);
		} finally {
			setAssetsLoading(false);
		}
	};

	// Fetch assets when store changes
	useEffect(() => {
		if (filters?.store?.id && type !== 'PROCESS_REPORT') {
			setAssetId(null);
			fetchAssetsList(filters.store.id);
		}
	}, [filters?.store?.id, type]);

	useEffect(() => {
		dispatch(drawerActions.toggleFiltersDrawer(true));
		resetInventoryAnalyticsState({
			entity: 'INVENTORY_ANALYTICS',
			method: 'SKU_STOCK',
			data: {},
		});
	}, [type]);

	const fetchStockReport = (filterValue) => {
		if (type === 'PROCESS_REPORT') {
			// Store and Process are both mandatory here: the backend report can't
			// be scoped (and errors out) without a store, and without a process
			// there is nothing process-level to show.
			if (!filterValue?.store?.id) {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Please select a store',
				});
				return;
			}
			if (filterValue?.processTypes?.id?.length === 0 || !filterValue?.processTypes) {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Please select a process',
				});
				return;
			}
		} else if (!filterValue?.store?.id || !filterValue?.tab) return;

		setFilters(filterValue);
		const req = {};
		if (type === 'PROCESS_REPORT') {
			req.params = {
				type: filterValue?.tab,
				storeId: filterValue?.store?.id,
				processId: filterValue?.processTypes?.id,
			};
		} else {
			req.params = {
				type: filterValue?.tab,
				storeId: filterValue?.store?.id,
				...(assetId && assetsList?.length > 0 && assetsList?.find((asset) => asset.value === assetId) && { assetId }),
				...(thirdPartySales !== '' && { thirdPartySales }),
			};
		}

		dispatch(analyticsAction.gateway({
			entity: 'INVENTORY_ANALYTICS',
			req,
			method: 'SKU_STOCK',
		}));
	};

	useEffect(() => {
		if (filters?.store?.id && filters?.tab && type !== 'PROCESS_REPORT') {
			fetchStockReport({ ...filters, assetId });
		}
	}, [assetId, thirdPartySales]);

	const handleExport = () => {
		const currDate = moment(new Date()).format('YYYY-MM-DD hh:mm');
		const headers = [
			'UY Variant ID',
			'SKU Name',
			'SKU Type',
			'SKU Code',
			'Category',
			'Sub Category',
			'Unit',
			'Item Rate',
			'Current Stock',
			'Stock worth',
			'Threshold',
			'Max Stock',
		];
		const totalValues = [];
		const filterData = (Array.isArray(data) ? data : Array?.from(data))?.filter((item) => {
			if (filters?.skuType === 'ALL' || !filters?.skuType) return true;
			return item?.skuType?.toLowerCase().includes(filters?.skuType?.toLowerCase());
		});
		filterData?.forEach((item) => {
			const skuName = item?.skuName?.split(',').join("'");
			const perUnit = convertToHigherUnit(item?.unit)?.unit;
			item?.currentStock?.forEach((stock, idx) => {
				const productName =
					skuName +
					(stock.variantName ? ` (${stock.variantName})` : '');
				// stock?.rate ? stock?.rate + ' per ' + perUnit : '-'
				const rate = stock?.rate ? stock?.rate : '-';
				const maxStock = item?.maxStock?.find((ele) => {
					return Number(ele?.variantId) === Number(stock?.variantId);
				})?.qty?.split(' ')[0] ?? '-';
				const threshold = item?.threshold?.find((ele) => {
					return Number(ele?.variantId) === Number(stock?.variantId);
				})?.qty?.split(' ')[0] ?? '-';
				totalValues?.push([
					`${stock?.variantId ?? '-'}`,
					`${productName}`,
					`${item?.skuType ?? '-'}`,
					`${stock?.clientVariantId ?? '-'}`,
					`${item?.categoryName ?? '-'}`,
					`${item?.subCategoryName ?? '-'}`,
					`${perUnit}`,
					`${rate}`,
					`${stock.qty.split(' ')[0] ?? '-'}`,
					`${stock?.stockAmount?.toFixed(2) ?? '-'}`,
					`${threshold}`,
					`${maxStock}`,
				]);
			});
		});
		handleExcelDownload(headers, totalValues, `Current Stock ${currDate}`);
	};

	// Backend report: batchwise data for skuType PRODUCT with available stock > 0.
	const handleBatchwiseExport = async () => {
		const currDate = moment(new Date()).format('YYYY-MM-DD hh:mm');
		try {
			const response = await inventoryAnalyticsServices.batchWiseSkuTypeExport({
				data: { skuType: 'PRODUCT' },
			});
			if (response?.ok) {
				await handleResponseStreamDownload(
					response,
					`Batchwise Stock PRODUCT ${currDate}.csv`
				);
				notificationServices.generateNotification({
					type: 'success',
					message: 'Exported successfully',
				});
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Some error occurred while exporting',
				});
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log('error', error);
			notificationServices.generateNotification({
				type: 'error',
				message: 'Some error occurred while exporting',
			});
		}
	};

	// Backend report: per-asset stock movement summary over a date range
	// (AssetProductStockDailySnapshot), retail stores only.
	const handleAssetWiseSummaryExport = async () => {
		const [from, to] = assetSummaryDateRange || [];
		if (!from || !to) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select a date range',
			});
			return;
		}
		if (disableTodayOrFutureDate(to)) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'End Date cannot be a current or future date',
			});
			return;
		}
		if (
			moment(to).startOf('day').diff(moment(from).startOf('day'), 'days') >
			ASSET_SUMMARY_MAX_RANGE_DAYS - 1
		) {
			notificationServices.generateNotification({
				type: 'error',
				message: `Date range cannot exceed ${ASSET_SUMMARY_MAX_RANGE_DAYS} days`,
			});
			return;
		}
		if (!filters?.store?.id) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select a store',
			});
			return;
		}
		const fromStr = moment(from).format('YYYY-MM-DD');
		const toStr = moment(to).format('YYYY-MM-DD');
		setAssetSummaryExporting(true);
		try {
			const response = await inventoryAnalyticsServices.assetWiseStockSummaryExport({
				data: {
					storeId: filters.store.id,
					dateRangeFilter: { from: fromStr, to: toStr },
				},
			});
			if (response?.ok) {
				await handleResponseStreamDownload(
					response,
					`Asset Wise Summary ${fromStr}_to_${toStr}.csv`
				);
				notificationServices.generateNotification({
					type: 'success',
					message: 'Exported successfully',
				});
				setAssetSummaryModalOpen(false);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Some error occurred while exporting',
				});
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log('error', error);
			notificationServices.generateNotification({
				type: 'error',
				message: 'Some error occurred while exporting',
			});
		} finally {
			setAssetSummaryExporting(false);
		}
	};

	// Routes the drawer export button by the selected report type dropdown.
	const handleReportExport = (req) => {
		if (req?.exportType === 'BATCH_WISE') {
			handleBatchwiseExport();
		} else if (req?.exportType === 'ASSET_WISE_SUMMARY') {
			setAssetSummaryDateRange([]);
			setAssetSummaryModalOpen(true);
		} else {
			handleExport();
		}
	};

	const filtersInfo = {
		header: {
			store: false,
			product: false,
			process: type !== 'PROCESS_REPORT',
			statusAndTypes: false,
		},
		store: {
			show: true,
			permittedStore: true,
			text: type === 'PROCESS_REPORT' ? 'Store *' : 'Store',
			mandatory: type === 'PROCESS_REPORT',
		},
		...(type === 'PROCESS_REPORT' ? {
			processType: {
				show: true,
				text: 'process',
				mandatory: true,
			},
		} : {}),
		skuType: {
			show: true,
			text: 'SKU Type',
		},
		tab: {
			show: true,
			list: [
				{
					id: 'stock',
					type: 'Stocked',
				},
				{
					id: 'rejected',
					type: 'Rejected',
				},
			],
		},
	};

	return (
		<div className={styles.root}>
			<ImagesFinder
				filtersInfo={filtersInfo}
				onSubmit={fetchStockReport}
				exportInfo={exportInfo}
				allStoreOption={false}
			/>
			<p className='text-lg font-bold m-1'>{getCurrentPageNavInfo(navState)?.subMenu?.name}</p>
			<div className='flex flex-1 items-baseline gap-3'>
				<div className='flex-1 min-w-0'>
					<GlobalSearch
						placeholder='Search By Name'
						searchDropDown={false}
						setSearchValue={setSearch}
					/>
				</div>
				{type !== 'PROCESS_REPORT' && assetsList?.length > 0 && (
					<SelectPicker
						data={assetsList}
						value={assetId}
						onChange={(value) => handleAssetChange(value)}
						placeholder='Select Asset'
						searchable
						cleanable
						loading={assetsLoading}
						style={{ width: 200 }}
						disabled={!filters?.store?.id}
					/>
				)}
				{type !== 'PROCESS_REPORT' && (
					<TextField
						select
						size='small'
						label='Third Party Sales'
						value={thirdPartySales}
						onChange={(e) => setThirdPartySales(e.target.value)}
						style={{ minWidth: 150 }}
						InputLabelProps={{ shrink: true }}
						SelectProps={{ displayEmpty: true }}
					>
						<MenuItem value=''>ALL</MenuItem>
						<MenuItem value='true'>Yes</MenuItem>
						<MenuItem value='false'>No</MenuItem>
					</TextField>
				)}
				{type !== 'PROCESS_REPORT' && (
					<button
						onClick={() => setStockDeficit(!stockDeficit)}
						className={`px-5 py-2 text-xs font-bold rounded transition-all ${stockDeficit
							? 'bg-slate-700 text-white'
							: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
							}
              `}
					>
						DEFICIT
					</button>
				)}
				{type !== 'PROCESS_REPORT' && (
					<div className='flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-full'>
						<span className='w-3 h-3 bg-red-500 rounded-full' />
						<span className='text-xs text-gray-700 whitespace-nowrap'>
							Current Stock &lt; Threshold
						</span>
					</div>
				)}
				<Button
					onClick={() => {
						dispatch(drawerActions.toggleFiltersDrawer(true, {
							handleDataExport: handleReportExport,
						}));
					}}
					className='flex items-center gap-2 px-4 py-2 text-white bg-teal-600 hover:bg-teal-700 rounded text-sm font-medium transition-colors'
				>
					<span className='text-nowrap'>Filter & Export</span>
					<Filter className='size-4' />
				</Button>
			</div>
			{filtersInfoChip(filtersInfo, filters)}
			<Dialog
				open={assetSummaryModalOpen}
				onClose={() => setAssetSummaryModalOpen(false)}
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle>Asset Wise Summary Export</DialogTitle>
				<DialogContent>
					<p className='text-xs text-gray-500 mb-3'>
						Select a date range of up to {ASSET_SUMMARY_MAX_RANGE_DAYS} days.
						End Date cannot be a current or future date.
					</p>
					<DateRangePicker
						value={assetSummaryDateRange?.length ? assetSummaryDateRange : null}
						onChange={(value) => setAssetSummaryDateRange(value || [])}
						shouldDisableDate={assetSummaryShouldDisableDate}
						format='dd-MM-yyyy'
						isoWeek
						style={{ width: '100%' }}
						menuStyle={{ zIndex: 9999 }}
						placeholder='Start Date  --  End Date'
						cleanable={false}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setAssetSummaryModalOpen(false)}
						className='px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded'
					>
						Cancel
					</Button>
					<Button
						onClick={handleAssetWiseSummaryExport}
						disabled={
							!assetSummaryDateRange?.[0] ||
							!assetSummaryDateRange?.[1] ||
							assetSummaryExporting
						}
						className='px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50'
					>
						{assetSummaryExporting ? 'Exporting...' : 'Export (.csv)'}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}

const mapStateToProps = (state) => {
	const data = state.analytics.INVENTORY_ANALYTICS.SKU_STOCK;
	const { navState, permittedStores } = state.navigation;
	return {
		data,
		navState,
		permittedStores,
	};
};

const mapDispatchToProps = {
	resetInventoryAnalyticsState: analyticsAction.resetInventoryAnalyticsState,
};

export default connect(mapStateToProps, mapDispatchToProps)(ImsStockFilter);
